import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { Building } from "src/components/shared-components/class/general-classes/building";

/**
 * Informative popup for a resident who has an open voting still to vote, so the
 * pending vote cannot be missed.
 *
 * Two modes:
 *  - single (no `condominiums` input): shown once per area entry from the
 *    condominium dashboard; dismisses with `{ goToVoting: true }` when the
 *    resident accepts the call-to-action.
 *  - list (`condominiums` provided): shown up-front from the building list when
 *    several condominiums have a pending vote; each row dismisses with
 *    `{ building }` so the caller can select it and open its voting list.
 */
@Component({
    selector: "app-popup-voting-alert",
    templateUrl: "./popup-voting-alert.component.html",
    styleUrls: ["./popup-voting-alert.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupVotingAlertComponent implements OnInit, OnDestroy {
  // When provided, the popup renders the clickable list of condominiums with a
  // pending vote instead of the single call-to-action.
  @Input() condominiums: Array<Building> = null;

  public titleLabel: string = "";
  public titlePluralLabel: string = "";
  public messageLabel: string = "";
  public listMessageSingularLabel: string = "";
  public listMessagePluralLabel: string = "";
  public goLabel: string = "";
  public laterLabel: string = "";

  private _translationSubscription: Subscription = null;

  constructor(
    private _modalController: ModalController,
    private _translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadTranslation();
    this.subscribeTranslationEvent();
  }

  ngOnDestroy(): void {
    this._translationSubscription?.unsubscribe();
  }

  /**
   * Whether the popup is in the condominium-list mode.
   */
  public get isListMode(): boolean {
    return Array.isArray(this.condominiums) && this.condominiums.length > 0;
  }

  /**
   * Whether more than one condominium has a pending vote (drives singular vs
   * plural wording).
   */
  private get isPlural(): boolean {
    return (this.condominiums?.length || 0) > 1;
  }

  /**
   * Title shown in list mode, singular/plural depending on the count.
   */
  public get listTitleLabel(): string {
    return this.isPlural ? this.titlePluralLabel : this.titleLabel;
  }

  /**
   * Intro message shown in list mode, singular/plural depending on the count.
   */
  public get listMessageLabel(): string {
    return this.isPlural
      ? this.listMessagePluralLabel
      : this.listMessageSingularLabel;
  }

  public onLater(): void {
    this._modalController.dismiss({ goToVoting: false });
  }

  public onGo(): void {
    this._modalController.dismiss({ goToVoting: true });
  }

  /**
   * List mode: the resident picked a condominium; hand it back so the caller
   * can select it and open its voting list.
   */
  public onSelectCondominium(building: Building): void {
    this._modalController.dismiss({ building });
  }

  private loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.votingAlertTitle",
        "dictionary.votingAlertTitlePlural",
        "dictionary.votingAlertMessage",
        "dictionary.votingAlertListMessageSingular",
        "dictionary.votingAlertListMessagePlural",
        "dictionary.votingAlertGo",
        "dictionary.votingAlertLater",
      ])
      .subscribe((translation) => {
        this.titleLabel = translation["dictionary.votingAlertTitle"];
        this.titlePluralLabel =
          translation["dictionary.votingAlertTitlePlural"];
        this.messageLabel = translation["dictionary.votingAlertMessage"];
        this.listMessageSingularLabel =
          translation["dictionary.votingAlertListMessageSingular"];
        this.listMessagePluralLabel =
          translation["dictionary.votingAlertListMessagePlural"];
        this.goLabel = translation["dictionary.votingAlertGo"];
        this.laterLabel = translation["dictionary.votingAlertLater"];
      });
  }

  private subscribeTranslationEvent(): void {
    this._translationSubscription =
      this._translateService.onLangChange.subscribe(() => {
        this.loadTranslation();
      });
  }
}
